import { useEffect, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  Replace,
  WalletCards,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import {
  PILA_STATUS_BADGE_VARIANT,
  formatPilaDate,
  formatPilaShortDate,
  intlLocale,
} from "@/components/payroll/pila/pila-format";
import { ApiError } from "@/lib/api";
import { useBankAccounts } from "@/lib/api/banking.queries";
import {
  downloadPilaFile,
  downloadPilaReceipt,
  formatPilaPeriod,
  type PilaBankChoice,
  type PilaPlanillaDetail,
  type PilaValidationIssue,
} from "@/lib/api/payroll-pila";
import {
  usePilaAvailability,
  usePilaPlanilla,
  useRefreshPilaStatus,
  useReplacePilaPlanilla,
  useRequestPilaPaymentUrl,
} from "@/lib/api/payroll-pila.queries";
import { useActiveCompany } from "@/lib/company-context";
import { downloadBlob } from "@/lib/download-blob";
import { useMoneyFormatter } from "@/lib/money-format";
import { useTeamPermission } from "@/lib/team-context";
import { translateApiError } from "@/lib/translate-api-error";

export const Route = createFileRoute(
  "/companies/$companyId/nomina/pila/$planillaId",
)({
  component: PilaPlanillaPage,
});

/**
 * While a payment is pending, ask PagoSimple every 10 s with the tab visible (PF-491) and on
 * return to the tab; a visit stops after 60 checks (10 minutes), the hourly check covers the rest.
 */
const AUTO_REFRESH_MS = 10_000;
const AUTO_REFRESH_MAX = 60;

function PilaPlanillaPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PilaPlanillaContent />
      </AppShell>
    </RequireAuth>
  );
}

function PilaPlanillaContent() {
  const { t } = useTranslation();
  const { companyId, planillaId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const availability = usePilaAvailability(companyId);
  const planillaQ = usePilaPlanilla(
    availability.data?.enabled ? companyId : undefined,
    planillaId,
  );

  const back = (
    <Link
      to="/companies/$companyId/nomina/pila"
      params={{ companyId }}
      className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3 w-3" />
      {t("pila.detail.back")}
    </Link>
  );

  if (
    availability.isPending ||
    (availability.data?.enabled && planillaQ.isPending)
  ) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!availability.data?.enabled) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {back}
        <ErrorState
          title={t("pila.disabled.title")}
          description={t("pila.disabled.body")}
        />
      </div>
    );
  }
  if (planillaQ.isError || !planillaQ.data) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        {back}
        <ErrorState
          title={t("pila.detail.error_title")}
          error={planillaQ.error}
          onRetry={() => void planillaQ.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      {back}
      <PlanillaView
        companyId={companyId}
        planilla={planillaQ.data}
        onReload={() => void planillaQ.refetch()}
      />
    </div>
  );
}

function PlanillaView({
  companyId,
  planilla,
  onReload,
}: {
  companyId: string;
  planilla: PilaPlanillaDetail;
  onReload: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.language);
  const formatCop = useMoneyFormatter();
  const navigate = useNavigate();
  const canWrite = useTeamPermission("payroll.write");
  const banks = useBankAccounts(
    planilla.actions.pay && canWrite ? companyId : undefined,
  );
  const pay = useRequestPilaPaymentUrl(companyId);
  const refresh = useRefreshPilaStatus(companyId);
  const replace = useReplacePilaPlanilla(companyId);
  const [bankDialog, setBankDialog] = useState<{
    accounts: PilaBankChoice[];
    selected: string;
  } | null>(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<"file" | "receipt" | null>(
    null,
  );

  // Ask PagoSimple while the payment is pending: on return to the tab and every 30 s while visible.
  const attempts = useRef(0);
  const inFlight = useRef(false);
  const refreshMutate = refresh.mutateAsync;
  useEffect(() => {
    if (planilla.status !== "awaiting_payment") return;
    const tick = () => {
      if (
        document.visibilityState !== "visible" ||
        inFlight.current ||
        attempts.current >= AUTO_REFRESH_MAX
      )
        return;
      attempts.current += 1;
      inFlight.current = true;
      refreshMutate({ planillaId: planilla.id })
        .then((result) => {
          if (result.outcome === "paid")
            toast.success(t("pila.detail.paid_toast"));
        })
        .catch(() => undefined)
        .finally(() => {
          inFlight.current = false;
        });
    };
    const interval = window.setInterval(tick, AUTO_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [planilla.status, planilla.id, refreshMutate, t]);

  const activeBanks = (banks.data?.accounts ?? []).filter(
    (account) => account.isActive,
  );

  function startPayment() {
    if (activeBanks.length > 1) {
      setBankDialog({
        accounts: activeBanks.map((account) => ({
          id: account.id,
          name: account.name,
        })),
        selected: planilla.bankAccountId ?? "",
      });
      return;
    }
    void payWith(activeBanks[0]?.id ?? null);
  }

  /** Opens the tab inside the click so the browser does not block it, then points it at PSE. */
  async function payWith(bankAccountId: string | null) {
    const tab = window.open("", "_blank");
    try {
      const result = await pay.mutateAsync({
        planillaId: planilla.id,
        bankAccountId,
      });
      setBankDialog(null);
      setPaymentUrl(result.url);
      if (tab) {
        tab.opener = null;
        tab.location.href = result.url;
      }
      toast.success(t("pila.detail.payment_opened"));
    } catch (error) {
      tab?.close();
      if (
        error instanceof ApiError &&
        error.code === "PILA_BANK_ACCOUNT_REQUIRED"
      ) {
        const accounts = (
          (error.details as { accounts?: PilaBankChoice[] } | undefined)
            ?.accounts ?? []
        ).slice();
        setBankDialog({ accounts, selected: "" });
        return;
      }
      toast.error(translateApiError(error, t, "pila.error"));
    }
  }

  async function onRefresh() {
    try {
      const result = await refresh.mutateAsync({ planillaId: planilla.id });
      toast[result.outcome === "paid" ? "success" : "info"](
        t(`pila.detail.refresh_${result.outcome}`),
      );
    } catch (error) {
      toast.error(translateApiError(error, t, "pila.error"));
    }
  }

  async function onReplace() {
    try {
      const next = await replace.mutateAsync({ planillaId: planilla.id });
      setReplaceOpen(false);
      toast.success(t("pila.detail.replaced"));
      void navigate({
        to: "/companies/$companyId/nomina/pila/$planillaId",
        params: { companyId, planillaId: next.id },
      });
    } catch (error) {
      toast.error(translateApiError(error, t, "pila.error"));
    }
  }

  async function onDownload(kind: "file" | "receipt") {
    setDownloading(kind);
    try {
      if (kind === "file") {
        downloadBlob(
          await downloadPilaFile(companyId, planilla.id),
          `pila_E_${planilla.period}.txt`,
        );
      } else {
        downloadBlob(
          await downloadPilaReceipt(companyId, planilla.id),
          `comprobante_pila_${planilla.period}_${planilla.payrollNumber ?? planilla.id}.pdf`,
        );
        onReload();
      }
    } catch (error) {
      toast.error(translateApiError(error, t, "pila.error"));
    } finally {
      setDownloading(null);
    }
  }

  const total = planilla.totalToPay ?? planilla.estimatedTotal ?? 0;
  const title = formatPilaPeriod(planilla.period, locale);

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("pila.detail.title")}{" "}
              <span className="capitalize">{title}</span>
            </h1>
            <Badge
              variant={PILA_STATUS_BADGE_VARIANT[planilla.status]}
              data-testid="pila-status"
            >
              {t(`pila.status.${planilla.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {planilla.payrollNumber
              ? t("pila.detail.number", { number: planilla.payrollNumber })
              : t("pila.detail.no_number")}
            {" · "}
            {t("pila.detail.health_period", {
              health: formatPilaPeriod(planilla.healthPeriod, locale),
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {planilla.actions.receipt ? (
            <Button
              variant="outline"
              onClick={() => void onDownload("receipt")}
              disabled={downloading === "receipt"}
              data-testid="pila-receipt"
            >
              {downloading === "receipt" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {t("pila.detail.receipt")}
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={() => void onDownload("file")}
            disabled={downloading === "file"}
          >
            {downloading === "file" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {t("pila.detail.file")}
          </Button>
          {planilla.actions.refresh ? (
            <Button
              variant="outline"
              onClick={() => void onRefresh()}
              disabled={refresh.isPending}
              data-testid="pila-refresh"
            >
              {refresh.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {t("pila.detail.refresh")}
            </Button>
          ) : null}
          {planilla.actions.replace && canWrite ? (
            <Button variant="outline" onClick={() => setReplaceOpen(true)}>
              <Replace className="h-4 w-4" />
              {t("pila.detail.replace")}
            </Button>
          ) : null}
          {planilla.actions.pay && canWrite ? (
            <Button
              onClick={startPayment}
              disabled={pay.isPending || banks.isPending}
              data-testid="pila-pay"
            >
              {pay.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <WalletCards className="h-4 w-4" />
              )}
              {planilla.status === "awaiting_payment"
                ? t("pila.detail.pay_again")
                : t("pila.detail.pay")}
            </Button>
          ) : null}
        </div>
      </div>

      <StatusBanner companyId={companyId} planilla={planilla} locale={locale} />

      {paymentUrl && planilla.status === "awaiting_payment" ? (
        <Alert variant="info">
          <ExternalLink className="h-4 w-4" />
          <AlertTitle>{t("pila.detail.payment_tab_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.detail.payment_tab_body")}{" "}
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary hover:underline"
            >
              {t("pila.detail.payment_tab_link")}
            </a>
          </AlertDescription>
        </Alert>
      ) : null}

      {!planilla.paidStatusConfigured &&
      (planilla.status === "validated" ||
        planilla.status === "awaiting_payment") ? (
        <Alert variant="warning" data-testid="pila-paid-unconfirmed">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("pila.detail.paid_unconfigured_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.detail.paid_unconfigured_body")}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label={
            planilla.totalToPay === null
              ? t("pila.detail.estimated_total")
              : t("pila.detail.total_to_pay")
          }
          value={formatCop(total)}
          testId="pila-total"
        />
        <SummaryCard
          label={t("pila.detail.arrear")}
          value={formatCop(planilla.arrearValue)}
        />
        <SummaryCard
          label={t("pila.detail.limit_date")}
          value={formatPilaDate(planilla.limitDate, locale)}
        />
        <SummaryCard
          label={t("pila.detail.contributors")}
          value={String(planilla.contributors)}
        />
      </div>

      <ValidationCard planilla={planilla} />

      {planilla.administrators.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("pila.detail.administrators")}
            </CardTitle>
            <CardDescription className="text-xs">
              {t("pila.detail.administrators_hint")}
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("pila.detail.admin_type")}</TableHead>
                  <TableHead>{t("pila.detail.admin_name")}</TableHead>
                  <TableHead className="text-right">
                    {t("pila.detail.admin_affiliates")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pila.detail.arrear")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("pila.detail.admin_total")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {planilla.administrators.map((admin) => (
                  <TableRow
                    key={`${admin.administratorType}-${admin.administratorCode}`}
                  >
                    <TableCell className="text-sm">
                      {admin.administratorType}
                    </TableCell>
                    <TableCell className="text-sm">
                      {admin.administratorName}{" "}
                      <span className="font-mono text-xs text-muted-foreground">
                        ({admin.administratorCode})
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {admin.affiliates}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(admin.arrearValue)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(admin.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("pila.detail.contributors_title")}
          </CardTitle>
          <CardDescription className="text-xs">
            {t("pila.detail.contributors_hint")}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("pila.detail.employee")}</TableHead>
                <TableHead className="text-right">
                  {t("pila.detail.days")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.detail.ibc")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.totals.pension")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.totals.health")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.totals.arl")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.totals.ccf")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.totals.sena_icbf")}
                </TableHead>
                <TableHead className="text-right">
                  {t("pila.detail.contributor_total")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planilla.contributorsDetail.map((c) => (
                <TableRow key={c.employeeId}>
                  <TableCell>
                    <Link
                      to="/companies/$companyId/nomina/empleados/$employeeId"
                      params={{ companyId, employeeId: c.employeeId }}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {c.name}
                    </Link>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                      <span className="font-mono">
                        {c.documentType} {c.document}
                      </span>
                      {c.novelties.ing ? (
                        <Badge variant="outline">
                          {t("pila.detail.novelty_ing")}
                        </Badge>
                      ) : null}
                      {c.novelties.ret ? (
                        <Badge variant="outline">
                          {t("pila.detail.novelty_ret")}
                        </Badge>
                      ) : null}
                      {c.novelties.vst ? (
                        <Badge variant="outline">
                          {t("pila.detail.novelty_vst")}
                        </Badge>
                      ) : null}
                      {c.exonerated ? (
                        <Badge variant="muted">
                          {t("pila.detail.exonerated")}
                        </Badge>
                      ) : null}
                      {c.highRiskPension ? (
                        <Badge variant="warning">
                          {t("pila.detail.high_risk")}
                        </Badge>
                      ) : null}
                      {(c.records ?? [])
                        .filter((r) => r.kind !== "base")
                        .map((r) => (
                          <Badge
                            key={`${r.kind}-${r.startDate}`}
                            variant="secondary"
                            title={t("pila.detail.record_ibc", {
                              ibc: formatCop(r.ibc),
                              total: formatCop(r.total),
                            })}
                            data-testid={`pila-record-${r.kind}`}
                          >
                            {t(`pila.detail.record_${r.kind}`, {
                              from: formatPilaShortDate(r.startDate, locale),
                              to: formatPilaShortDate(r.endDate, locale),
                              days: r.days,
                            })}
                          </Badge>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {c.days}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.ibc)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.pension + c.fspSolidarity + c.fspSubsistence)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.health)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.arl)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.ccf)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatCop(c.sena + c.icbf)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-medium">
                    {formatCop(c.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {planilla.journalEntryId ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("pila.detail.accounting")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DocumentJournalPanel
              companyId={companyId}
              sourceTable="pila_planillas"
              documentId={planilla.id}
            />
          </CardContent>
        </Card>
      ) : null}

      <Dialog
        open={!!bankDialog}
        onOpenChange={(open) => (open ? null : setBankDialog(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pila.detail.bank_title")}</DialogTitle>
            <DialogDescription>{t("pila.detail.bank_body")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pila-bank">{t("pila.detail.bank_label")}</Label>
            <Select
              value={bankDialog?.selected ?? ""}
              onValueChange={(selected) =>
                setBankDialog((dialog) =>
                  dialog ? { ...dialog, selected } : dialog,
                )
              }
            >
              <SelectTrigger id="pila-bank">
                <SelectValue placeholder={t("pila.detail.bank_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {(bankDialog?.accounts ?? []).map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialog(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() =>
                bankDialog?.selected && void payWith(bankDialog.selected)
              }
              disabled={!bankDialog?.selected || pay.isPending}
            >
              {pay.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <WalletCards className="h-4 w-4" />
              )}
              {t("pila.detail.pay")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pila.detail.replace_title")}</DialogTitle>
            <DialogDescription>
              {t("pila.detail.replace_body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void onReplace()}
              disabled={replace.isPending}
            >
              {replace.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Replace className="h-4 w-4" />
              )}
              {t("pila.detail.replace_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SummaryCard({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p
          className="mt-1 font-mono text-xl font-semibold"
          data-testid={testId}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function StatusBanner({
  companyId,
  planilla,
  locale,
}: {
  companyId: string;
  planilla: PilaPlanillaDetail;
  locale: string;
}) {
  const { t } = useTranslation();
  const limit = formatPilaDate(planilla.limitDate, locale);
  switch (planilla.status) {
    case "validating":
      return (
        <Alert variant="info">
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>{t("pila.banner.validating_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.banner.validating_body")}
          </AlertDescription>
        </Alert>
      );
    case "validated":
      return (
        <Alert variant="info">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>{t("pila.banner.validated_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.banner.validated_body", { date: limit })}
          </AlertDescription>
        </Alert>
      );
    case "awaiting_payment":
      return (
        <Alert variant="info">
          <Clock className="h-4 w-4" />
          <AlertTitle>{t("pila.banner.awaiting_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.banner.awaiting_body", { date: limit })}
          </AlertDescription>
        </Alert>
      );
    case "paid":
      return (
        <Alert
          variant="info"
          className="border-emerald-500/40 bg-emerald-50 dark:bg-emerald-900/20"
        >
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>{t("pila.banner.paid_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.banner.paid_body", {
              date: formatPilaDate(planilla.paidConfirmedAt, locale),
            })}
          </AlertDescription>
        </Alert>
      );
    case "rejected":
      return (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {planilla.providerError
              ? t("pila.banner.provider_error_title")
              : t("pila.banner.rejected_title")}
          </AlertTitle>
          <AlertDescription className="text-xs text-foreground">
            {planilla.providerError ? (
              <span>
                {planilla.providerError.providerError}{" "}
                {planilla.providerError.retryable
                  ? t("pila.banner.provider_error_retry")
                  : null}
              </span>
            ) : (
              t("pila.banner.rejected_body")
            )}{" "}
            <Link
              to="/companies/$companyId/nomina/pila"
              params={{ companyId }}
              className="font-medium text-primary hover:underline"
            >
              {t("pila.banner.back_to_month")}
            </Link>
          </AlertDescription>
        </Alert>
      );
    case "expired":
      return (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("pila.banner.expired_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {t("pila.banner.expired_body", { date: limit })}
          </AlertDescription>
        </Alert>
      );
    case "superseded":
      return (
        <Alert>
          <Replace className="h-4 w-4" />
          <AlertTitle>{t("pila.banner.superseded_title")}</AlertTitle>
          <AlertDescription className="text-xs">
            {planilla.supersededById ? (
              <Link
                to="/companies/$companyId/nomina/pila/$planillaId"
                params={{ companyId, planillaId: planilla.supersededById }}
                className="font-medium text-primary hover:underline"
              >
                {t("pila.banner.superseded_open")}
              </Link>
            ) : null}
          </AlertDescription>
        </Alert>
      );
  }
}

function ValidationCard({ planilla }: { planilla: PilaPlanillaDetail }) {
  const { t } = useTranslation();
  const errors = planilla.validation?.errors ?? [];
  const warnings = planilla.validation?.warnings ?? [];
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <Card data-testid="pila-validation">
      <CardHeader>
        <CardTitle className="text-base">
          {t("pila.detail.validation_title")}
        </CardTitle>
        <CardDescription className="text-xs">
          {t("pila.detail.validation_hint")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {errors.length > 0 ? (
          <IssueList
            title={t("pila.detail.validation_errors")}
            issues={errors}
            tone="error"
          />
        ) : null}
        {warnings.length > 0 ? (
          <IssueList
            title={t("pila.detail.validation_warnings")}
            issues={warnings}
            tone="warning"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function IssueList({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: PilaValidationIssue[];
  tone: "error" | "warning";
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p
        className={
          tone === "error"
            ? "text-sm font-medium text-destructive"
            : "text-sm font-medium text-amber-700 dark:text-amber-300"
        }
      >
        {title}
      </p>
      <ul className="mt-2 space-y-1.5 text-sm">
        {issues.map((issue, index) => (
          <li key={index} className="rounded-md border px-3 py-2">
            <span>{issue.description}</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {[
                issue.employeeName,
                issue.field
                  ? t("pila.detail.validation_field", { field: issue.field })
                  : null,
                issue.row
                  ? t("pila.detail.validation_row", { row: issue.row })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
